import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { 
  Building2, 
  UserCircle, 
  Users2, 
  MessageSquare,
  Upload,
  Save,
  Instagram,
  Phone,
  MapPin,
  FileText,
  Mail,
  Shield,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Hash,
  Copy,
  Clock,
  Send,
  Link2,
  Bot,
  MessageCircle,
  Sparkles,
  CalendarClock
} from "lucide-react";
import { TatuadorForm } from "@/components/TatuadorForm";
import { useUserRole } from "@/hooks/useUserRole";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export const Route = createFileRoute("/configuracoes")({
  component: ConfiguracoesPage,
});

const MENU_ITEMS = [
  { id: "perfil", label: "Perfil do Studio", icon: Building2 },
  { id: "conta", label: "Minha Conta", icon: UserCircle },
  { id: "equipe", label: "Minha Equipe", icon: Users2 },
  { id: "mensagens", label: "WhatsApp Automático", icon: MessageCircle },
  { id: "assistente", label: "Assistente", icon: Bot },
];


function ConfiguracoesPage() {
  const { isTatuador } = useUserRole();
  const [activeSection, setActiveSection] = useState("perfil");

  const menuItems = MENU_ITEMS.filter((i) => i.id !== "equipe" || !isTatuador);

  useEffect(() => {
    if (isTatuador && activeSection === "equipe") setActiveSection("perfil");
  }, [isTatuador, activeSection]);

  // Tatuadores State
  const [tatuadores, setTatuadores] = useState<any[]>([]);
  const [isTatuadorDialogOpen, setIsTatuadorDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingTatuador, setEditingTatuador] = useState<any>(null);
  const [tatuadorToDelete, setTatuadorToDelete] = useState<string | null>(null);
  const [isLoadingEquipe, setIsLoadingEquipe] = useState(true);
  const [conviteGerado, setConviteGerado] = useState<string | null>(null);

  const fetchEquipe = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: equipeData, error } = await supabase
        .from("equipe")
        .select(`*, usuarios:tatuador_id ( id, nome, email, telefone )`)
        .eq("studio_id", session.user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setTatuadores(
        (equipeData || []).map((e) => ({
          id: e.id,
          tatuador_id: e.tatuador_id,
          codigo_convite: e.codigo_convite,
          nome: e.usuarios?.nome || e.nome || "—",
          email: e.usuarios?.email || e.email || "",
          telefone: e.usuarios?.telefone || e.telefone || "",
          instagram: e.instagram || "",
          observacoes: e.observacoes || "",
          comissao: Number(e.comissao_percentual) || 0,
          permissao_clientes: e.permissao_clientes || "proprios",
          permissao_estoque: e.permissao_estoque || "nenhum",
          status: e.status || "pendente", // 'pendente' | 'ativo' | 'inativo'
        }))
      );
    } catch (err) {
      console.error("Erro ao carregar equipe:", err);
    } finally {
      setIsLoadingEquipe(false);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setIsLoadingEquipe(true);
    setIsLoadingProfile(true);
    await Promise.all([
      fetchEquipe(),
      fetchProfileData()
    ]);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useRealtimeSubscription("equipe", fetchEquipe);

  const inviteUrl = (codigo: string) =>
    `${window.location.origin}/cadastro?convite=${codigo}&tipo=tatuador`;

  const handleOpenTatuadorDialog = (tatuador: any = null) => {
    setEditingTatuador(tatuador);
    setConviteGerado(null);
    setIsTatuadorDialogOpen(true);
  };

  const handleSaveTatuador = async (form: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        toast.error("Você precisa estar logado");
        return;
      }

      if (editingTatuador) {
        const { error } = await supabase
          .from("equipe")
          .update({
            nome: form.nome,
            email: form.email,
            telefone: form.telefone,
            instagram: form.instagram,
            observacoes: form.observacoes,
            comissao_percentual: form.comissao_percentual,
            permissao_clientes: form.permissao_clientes,
            permissao_estoque: form.permissao_estoque,
          })
          .eq("id", editingTatuador.id);
        if (error) throw error;
        toast.success("Dados do tatuador atualizados!");
        setIsTatuadorDialogOpen(false);
        setEditingTatuador(null);
        fetchEquipe();
        return;
      }

      // Novo tatuador: cria o pré-cadastro na equipe + o convite
      const codigo = Math.random().toString(36).substring(2, 14).toUpperCase();
      const expiraEm = new Date();
      expiraEm.setDate(expiraEm.getDate() + 7);

      const { error: equipeError } = await supabase.from("equipe").insert({
        studio_id: session.user.id,
        tatuador_id: null,
        nome: form.nome,
        email: form.email,
        telefone: form.telefone,
        instagram: form.instagram,
        observacoes: form.observacoes,
        comissao_percentual: form.comissao_percentual,
        permissao_clientes: form.permissao_clientes,
        permissao_estoque: form.permissao_estoque,
        status: "pendente",
        codigo_convite: codigo,
      });
      if (equipeError) throw equipeError;

      const { error: conviteError } = await supabase.from("convites").insert({
        codigo,
        tipo: "tatuador",
        studio_dono_id: session.user.id,
        email_destinatario: form.email,
        expira_em: expiraEm.toISOString(),
        usado: false,
      });
      if (conviteError) throw conviteError;

      const link = inviteUrl(codigo);
      setConviteGerado(link);
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Convite gerado e link copiado!");
      } catch {
        toast.success("Convite gerado! Copie o link abaixo.");
      }
      fetchEquipe();
    } catch (err: any) {
      console.error("Erro ao salvar tatuador:", err);
      toast.error("Erro ao salvar: " + (err.message || "tente novamente"));
    }
  };

  const handleToggleStatusTatuador = async (t: any) => {
    const novo = t.status === "ativo" ? "inativo" : "ativo";
    const { error } = await supabase.from("equipe").update({ status: novo }).eq("id", t.id);
    if (error) {
      toast.error("Erro ao alterar status");
    } else {
      toast.success(`Tatuador ${novo === "ativo" ? "ativado" : "desativado"}`);
      fetchEquipe();
    }
  };

  const handleDeleteTatuador = async () => {
    if (!tatuadorToDelete) return;
    try {
      const alvo = tatuadores.find((t) => t.id === tatuadorToDelete);
      const { error } = await supabase.from("equipe").delete().eq("id", tatuadorToDelete);
      if (error) throw error;
      if (alvo?.codigo_convite && alvo.status === "pendente") {
        await supabase.from("convites").delete().eq("codigo", alvo.codigo_convite);
      }
      toast.success("Tatuador removido da equipe!");
      fetchEquipe();
    } catch (err) {
      console.error("Erro ao remover tatuador:", err);
      toast.error("Erro ao remover tatuador");
    } finally {
      setIsDeleteDialogOpen(false);
      setTatuadorToDelete(null);
    }
  };
  
  // Studio Profile State
  const [studioProfile, setStudioProfile] = useState({
    nome: "",
    telefone: "",
    instagram: "",
    endereco: "",
    cnpj: "",
    logo_url: null as string | null,
    slug: "",
    linkAvaliacaoGoogle: "",
  });
  const [slugError, setSlugError] = useState<string | null>(null);
  const [isSavingSlug, setIsSavingSlug] = useState(false);

  // User Account State
  const [userAccount, setUserAccount] = useState({
    nome: "",
    email: "",
    telefone: "",
    cargo: ""
  });

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const fetchProfileData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Buscar perfil do studio
      const { data: studioData } = await supabase
        .from("configuracoes")
        .select("*")
        .eq("studio_id", session.user.id)
        .single();

      if (studioData) {
        setStudioProfile({
          nome: studioData.nome_studio || "",
          telefone: studioData.telefone || "",
          instagram: studioData.instagram || "",
          endereco: studioData.endereco || "",
          cnpj: studioData.cnpj || "",
          logo_url: studioData.logo_url || null,
          slug: studioData.slug || "",
          linkAvaliacaoGoogle: studioData.link_avaliacao_google || "",
        });
        setMessages((prev) => ({
          confirmacao: studioData.mensagem_confirmacao ?? prev.confirmacao,
          lembrete: studioData.mensagem_lembrete ?? prev.lembrete,
          cuidados: studioData.mensagem_pos_tattoo ?? prev.cuidados,
          cicatrizacao: studioData.mensagem_cicatrizacao ?? prev.cicatrizacao,
          reativacao: studioData.mensagem_reativacao ?? prev.reativacao,
          aniversario: studioData.mensagem_aniversario ?? prev.aniversario,
        }));
        setAssistenteAtivo(studioData.assistente_ia_ativo ?? false);
        setWhatsappConectado(studioData.whatsapp_conectado ?? false);
        setNotificacoesEmailAtivo(studioData.notificacoes_email_ativo ?? true);
      }

      // Buscar dados do usuário
      const { data: userData } = await supabase
        .from("usuarios")
        .select("*")
        .eq("id", session.user.id)
        .single();

      if (userData) {
        setUserAccount({
          nome: userData.nome || "",
          email: userData.email || "",
          telefone: userData.telefone || "",
          cargo: userData.role || ""
        });
      }
    } catch (err) {
      console.error("Erro ao carregar dados do perfil:", err);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `${session.user.id}-logo.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('studio_assets')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('studio_assets')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from("configuracoes")
        .upsert({ studio_id: session.user.id, logo_url: publicUrl }, { onConflict: 'studio_id' });

      if (updateError) throw updateError;

      setStudioProfile(prev => ({ ...prev, logo_url: publicUrl }));
      window.dispatchEvent(new CustomEvent('studio-info-updated'));
      toast.success("Logo atualizada com sucesso!");
    } catch (err: any) {
      console.error("Erro ao fazer upload da logo:", err);
      toast.error("Erro ao atualizar logo: " + err.message);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  // Password State
  const [passwordData, setPasswordData] = useState({
    atual: "",
    nova: "",
    confirmar: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    atual: false,
    nova: false,
    confirmar: false
  });

  // Messages State
  const [messages, setMessages] = useState({
    confirmacao: "Olá {nome_cliente}, seu agendamento no {nome_tatuador} está confirmado para o dia {data} às {horario}. O valor total é de {valor} com sinal de {sinal}. Adicione no seu calendário: {link_calendario}",
    lembrete: "Oi {nome_cliente}, passando para lembrar da sua tattoo amanhã às {horario} no {nome_tatuador}. Nos vemos lá!",
    cuidados: "E aí {nome_cliente}, como está a cicatrização? Lembre-se de seguir todos os cuidados que passamos no {nome_tatuador}. Qualquer dúvida, nos chame! Se puder, deixa sua avaliação: {link_avaliacao}",
    cicatrizacao: "Oi {nome_cliente}, já faz 15 dias da sua tattoo no {nome_tatuador}! Como está a cicatrização? Manda uma foto pra gente acompanhar o resultado 📸",
    reativacao: "Olá {nome_cliente}, quanto tempo! Notamos que faz mais de 3 meses da sua última visita ao {nome_tatuador}. Que tal uma ideia nova? Mande um oi!",
    aniversario: "Parabéns {nome_cliente}! 🎂 O {nome_tatuador} te deseja muitas felicidades. Como presente, temos um desconto especial para sua próxima tattoo neste mês!"
  });

  const [mensagemAtiva, setMensagemAtiva] = useState<keyof typeof messages>("confirmacao");

  const textareaRefs = {
    confirmacao: useRef<HTMLTextAreaElement>(null),
    lembrete: useRef<HTMLTextAreaElement>(null),
    cuidados: useRef<HTMLTextAreaElement>(null),
    cicatrizacao: useRef<HTMLTextAreaElement>(null),
    reativacao: useRef<HTMLTextAreaElement>(null),
    aniversario: useRef<HTMLTextAreaElement>(null),
  };

  // Assistente / WhatsApp
  const [assistenteAtivo, setAssistenteAtivo] = useState(false);
  const [whatsappConectado, setWhatsappConectado] = useState(false);
  const [isSavingAssistente, setIsSavingAssistente] = useState(false);
  const [notificacoesEmailAtivo, setNotificacoesEmailAtivo] = useState(true);
  const [isSavingNotificacoes, setIsSavingNotificacoes] = useState(false);

  const insertVariable = (key: keyof typeof messages, variable: string) => {
    const textarea = textareaRefs[key].current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = messages[key];
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    const newText = before + variable + after;
    setMessages({ ...messages, [key]: newText });

    // Reset focus and cursor position after state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const handleSaveMessages = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from("configuracoes")
        .update({
          mensagem_confirmacao: messages.confirmacao,
          mensagem_lembrete: messages.lembrete,
          mensagem_pos_tattoo: messages.cuidados,
          mensagem_cicatrizacao: messages.cicatrizacao,
          mensagem_reativacao: messages.reativacao,
          mensagem_aniversario: messages.aniversario,
        })
        .eq("studio_id", session.user.id);

      if (error) throw error;
      toast.success("Mensagens atualizadas com sucesso!");
    } catch (err: any) {
      console.error("Erro ao salvar mensagens:", err);
      toast.error("Erro ao salvar mensagens: " + err.message);
    }
  };

  const handleToggleAssistente = async (ativo: boolean) => {
    if (!whatsappConectado) {
      toast.error("Conecte o WhatsApp do studio antes de ativar o assistente.");
      return;
    }
    setIsSavingAssistente(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from("configuracoes")
        .update({ assistente_ia_ativo: ativo })
        .eq("studio_id", session.user.id);

      if (error) throw error;
      setAssistenteAtivo(ativo);
      toast.success(ativo ? "Assistente ativado!" : "Assistente desativado.");
    } catch (err: any) {
      console.error("Erro ao atualizar assistente:", err);
      toast.error("Erro ao atualizar: " + err.message);
    } finally {
      setIsSavingAssistente(false);
    }
  };

  const handleConectarWhatsapp = () => {
    toast.info("Conexão com WhatsApp em breve — ainda estamos configurando a integração oficial com a Meta.");
  };

  const handleToggleNotificacoesEmail = async (ativo: boolean) => {
    setIsSavingNotificacoes(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from("configuracoes")
        .update({ notificacoes_email_ativo: ativo })
        .eq("studio_id", session.user.id);

      if (error) throw error;
      setNotificacoesEmailAtivo(ativo);
      toast.success(ativo ? "Notificações por e-mail ativadas!" : "Notificações por e-mail desativadas.");
    } catch (err: any) {
      console.error("Erro ao atualizar notificações:", err);
      toast.error("Erro ao atualizar: " + err.message);
    } finally {
      setIsSavingNotificacoes(false);
    }
  };

  const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

  const validarSlug = (valor: string): string | null => {
    if (!valor.trim()) return "Defina um link para habilitar o cadastro público";
    if (!SLUG_REGEX.test(valor)) {
      return "Use apenas letras minúsculas, números e hífen (sem espaços, acentos ou hífen duplicado/nas pontas)";
    }
    return null;
  };

  const handleSlugChange = (valor: string) => {
    const normalizado = valor
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "") // remove acentos
      .replace(/\s+/g, "-") // espaço vira hífen
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-"); // colapsa hífens repetidos
    setStudioProfile({ ...studioProfile, slug: normalizado });
    setSlugError(null);
  };

  const handleSaveProfile = async () => {
    const erroSlug = validarSlug(studioProfile.slug);
    if (erroSlug) {
      setSlugError(erroSlug);
      toast.error(erroSlug);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      setIsSavingSlug(true);
      const { error } = await supabase
        .from("configuracoes")
        .upsert({
          studio_id: session.user.id,
          nome_studio: studioProfile.nome,
          telefone: studioProfile.telefone,
          instagram: studioProfile.instagram,
          endereco: studioProfile.endereco,
          cnpj: studioProfile.cnpj,
          slug: studioProfile.slug,
          link_avaliacao_google: studioProfile.linkAvaliacaoGoogle || null,
        }, { onConflict: 'studio_id' });

      if (error) {
        if (error.code === "23505" || error.message?.toLowerCase().includes("duplicate")) {
          const msg = "Esse link já está em uso por outro studio. Escolha outro.";
          setSlugError(msg);
          toast.error(msg);
          return;
        }
        throw error;
      }

      window.dispatchEvent(new CustomEvent('studio-info-updated'));
      toast.success("Perfil do studio atualizado com sucesso!");
      fetchProfileData();
    } catch (err) {
      console.error("Erro ao salvar perfil:", err);
      toast.error("Erro ao salvar perfil");
    } finally {
      setIsSavingSlug(false);
    }
  };

  const handleSaveAccount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase
        .from("usuarios")
        .update({
          nome: userAccount.nome,
          email: userAccount.email,
          telefone: userAccount.telefone,
        })
        .eq("id", session.user.id);

      if (error) throw error;
      toast.success("Dados da conta atualizados com sucesso!");
      fetchProfileData();
    } catch (err) {
      console.error("Erro ao salvar conta:", err);
      toast.error("Erro ao salvar conta");
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.atual || !passwordData.nova) {
      toast.error("Preencha os campos de senha");
      return;
    }
    if (passwordData.nova !== passwordData.confirmar) {
      toast.error("A nova senha e a confirmação devem ser iguais");
      return;
    }
    if (passwordData.nova.length < 8) {
      toast.error("A nova senha deve ter pelo menos 8 caracteres");
      return;
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) {
        toast.error("Sessão inválida. Faça login novamente.");
        return;
      }
      // Confirma a senha atual antes de trocar
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordData.atual,
      });
      if (reauthError) {
        toast.error("Senha atual incorreta");
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: passwordData.nova });
      if (error) {
        toast.error("Erro ao alterar senha: " + error.message);
        return;
      }
      toast.success("Senha alterada com sucesso!");
      setPasswordData({ atual: "", nova: "", confirmar: "" });
    } catch (err) {
      toast.error("Erro ao alterar senha");
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case "equipe": {
        const statusBadge = (s: string) =>
          s === "ativo"
            ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
            : s === "pendente"
            ? "bg-amber-500/10 text-amber-600 border-amber-500/20"
            : "bg-zinc-500/10 text-muted-foreground border-zinc-500/20";
        const statusLabel = (s: string) =>
          s === "ativo" ? "Ativo" : s === "pendente" ? "Aguardando" : "Inativo";

        return (
          <div className="space-y-6 animate-in fade-in duration-500">
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 space-y-0">
                <div>
                  <CardTitle className="text-xl font-semibold text-foreground">Minha Equipe</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Tatuadores que trabalham no seu studio e pagam comissão por isso
                  </CardDescription>
                </div>
                <Button
                  onClick={() => handleOpenTatuadorDialog(null)}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg shadow-primary/20 shrink-0"
                >
                  <Plus className="mr-2 size-4" />
                  Convidar Tatuador
                </Button>
              </CardHeader>
              <CardContent>
                {isLoadingEquipe ? (
                  <div className="p-10 text-center text-muted-foreground text-sm">Carregando equipe...</div>
                ) : tatuadores.length === 0 ? (
                  <div className="p-10 text-center text-muted-foreground text-sm italic border border-dashed border-border rounded-xl">
                    Nenhum tatuador na equipe. Clique em “Convidar Tatuador” para começar.
                  </div>
                ) : (
                  <div className="bg-background border border-border rounded-xl overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-border bg-muted/30">
                          <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Nome / Contato</th>
                          <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Comissão</th>
                          <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Status</th>
                          <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Convite</th>
                          <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {tatuadores.map((t) => (
                          <tr key={t.id} className="hover:bg-muted/30 transition-colors align-top">
                            <td className="p-4">
                              <p className="text-sm font-semibold text-foreground">{t.nome}</p>
                              <p className="text-xs text-muted-foreground">{t.email || "—"}</p>
                              {t.telefone && <p className="text-xs text-muted-foreground">{t.telefone}</p>}
                              {t.instagram && <p className="text-xs text-primary">{t.instagram}</p>}
                            </td>
                            <td className="p-4 text-sm font-semibold text-foreground">{t.comissao}%</td>
                            <td className="p-4">
                              <Badge className={cn("text-[10px] uppercase font-bold", statusBadge(t.status))}>
                                {statusLabel(t.status)}
                              </Badge>
                            </td>
                            <td className="p-4">
                              {t.status === "pendente" && t.codigo_convite ? (
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(inviteUrl(t.codigo_convite)).catch(() => {});
                                    toast.success("Link de convite copiado!");
                                  }}
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                                >
                                  <Copy size={12} /> Copiar link
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="p-4 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="size-8 text-muted-foreground hover:text-foreground">
                                    <MoreVertical size={14} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-card border-border">
                                  <DropdownMenuItem onClick={() => handleOpenTatuadorDialog(t)} className="text-foreground focus:bg-muted">
                                    <Pencil className="mr-2 size-4" /> Editar dados
                                  </DropdownMenuItem>
                                  {t.status !== "pendente" && (
                                    <DropdownMenuItem
                                      onClick={() => handleToggleStatusTatuador(t)}
                                      className="text-foreground focus:bg-muted"
                                    >
                                      {t.status === "ativo" ? <XCircle className="mr-2 size-4" /> : <CheckCircle2 className="mr-2 size-4" />}
                                      {t.status === "ativo" ? "Desativar" : "Ativar"}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setTatuadorToDelete(t.id);
                                      setIsDeleteDialogOpen(true);
                                    }}
                                    className="text-primary focus:bg-primary/10"
                                  >
                                    <Trash2 className="mr-2 size-4" /> Remover da equipe
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      }

      case "perfil":
        return (
          <div className="space-y-6 animate-in fade-in duration-500">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground">Perfil do Studio</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Informações públicas e de contato do seu studio
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  {/* Logo Upload */}
                  <div className="space-y-3">
                    <Label className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest">Logo do Studio</Label>
                    <div
                      className={`size-32 rounded-2xl bg-background border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 hover:border-primary transition-colors overflow-hidden group relative ${isUploadingLogo ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}
                      onClick={() => !isUploadingLogo && document.getElementById('logo-upload')?.click()}
                    >
                      {isUploadingLogo ? (
                        <span className="text-[10px] text-primary font-semibold animate-pulse">ENVIANDO...</span>
                      ) : studioProfile.logo_url ? (
                        <img src={studioProfile.logo_url} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <>
                          <Upload size={24} className="text-muted-foreground group-hover:text-primary transition-colors" />
                          <span className="text-[10px] text-muted-foreground font-semibold">UPLOAD</span>
                        </>
                      )}
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={isUploadingLogo}
                      />
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                    <div className="space-y-2">
                      <Label htmlFor="nome" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <Building2 size={12} className="text-primary" /> Nome do Studio
                      </Label>
                      <Input 
                        id="nome"
                        value={studioProfile.nome}
                        onChange={(e) => setStudioProfile({...studioProfile, nome: e.target.value})}
                        className="bg-background border-border text-foreground focus:border-primary transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <Phone size={12} className="text-primary" /> Telefone
                      </Label>
                      <Input 
                        id="telefone"
                        value={studioProfile.telefone}
                        onChange={(e) => setStudioProfile({...studioProfile, telefone: e.target.value})}
                        className="bg-background border-border text-foreground focus:border-primary transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="instagram" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <Instagram size={12} className="text-primary" /> Instagram
                      </Label>
                      <Input 
                        id="instagram"
                        value={studioProfile.instagram}
                        onChange={(e) => setStudioProfile({...studioProfile, instagram: e.target.value})}
                        className="bg-background border-border text-foreground focus:border-primary transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cnpj" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <FileText size={12} className="text-primary" /> CNPJ (Opcional)
                      </Label>
                      <Input 
                        id="cnpj"
                        value={studioProfile.cnpj}
                        onChange={(e) => setStudioProfile({...studioProfile, cnpj: e.target.value})}
                        className="bg-background border-border text-foreground focus:border-primary transition-all"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="endereco" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <MapPin size={12} className="text-primary" /> Endereço Completo
                      </Label>
                      <Input
                        id="endereco"
                        value={studioProfile.endereco}
                        onChange={(e) => setStudioProfile({...studioProfile, endereco: e.target.value})}
                        className="bg-background border-border text-foreground focus:border-primary transition-all"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="slug" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <Link2 size={12} className="text-primary" /> Link de cadastro público
                      </Label>
                      <Input
                        id="slug"
                        value={studioProfile.slug}
                        onChange={(e) => handleSlugChange(e.target.value)}
                        placeholder="meu-studio"
                        className={cn(
                          "bg-background border-border text-foreground focus:border-primary transition-all",
                          slugError && "border-red-500 focus:border-red-500"
                        )}
                      />
                      {slugError ? (
                        <p className="text-[10px] text-red-500 font-semibold flex items-center gap-1">
                          <AlertCircle size={10} /> {slugError}
                        </p>
                      ) : (
                        <p className="text-[10px] text-muted-foreground break-all">
                          {typeof window !== "undefined" ? window.location.origin : ""}/ficha/{studioProfile.slug || "meu-studio"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="link_avaliacao" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                        <Link2 size={12} className="text-primary" /> Link de avaliação Google
                      </Label>
                      <Input
                        id="link_avaliacao"
                        value={studioProfile.linkAvaliacaoGoogle}
                        onChange={(e) => setStudioProfile({...studioProfile, linkAvaliacaoGoogle: e.target.value})}
                        placeholder="https://g.page/r/..."
                        className="bg-background border-border text-foreground focus:border-primary transition-all"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Usado na variável {"{link_avaliacao}"} das mensagens de WhatsApp. Pega esse link no Google Meu Negócio.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={isSavingSlug}
                    className="bg-primary hover:bg-primary/90 text-white font-semibold h-11 px-8 shadow-lg shadow-primary/20 transition-all active:scale-95"
                  >
                    <Save className="mr-2 size-4" />
                    {isSavingSlug ? "Salvando..." : "Salvar Perfil"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case "conta":
        return (
          <div className="space-y-6 animate-in fade-in duration-500">
            {/* Account Info Card */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground">Minha Conta</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Gerencie seus dados pessoais e cargo no sistema
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="user-nome" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                      <UserCircle size={12} className="text-primary" /> Nome Completo
                    </Label>
                    <Input 
                      id="user-nome"
                      value={userAccount.nome}
                      onChange={(e) => setUserAccount({...userAccount, nome: e.target.value})}
                      className="bg-background border-border text-foreground focus:border-primary transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-email" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                      <Mail size={12} className="text-primary" /> E-mail
                    </Label>
                    <Input 
                      id="user-email"
                      type="email"
                      value={userAccount.email}
                      onChange={(e) => setUserAccount({...userAccount, email: e.target.value})}
                      className="bg-background border-border text-foreground focus:border-primary transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-telefone" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                      <Phone size={12} className="text-primary" /> Telefone
                    </Label>
                    <Input 
                      id="user-telefone"
                      value={userAccount.telefone}
                      onChange={(e) => setUserAccount({...userAccount, telefone: e.target.value})}
                      className="bg-background border-border text-foreground focus:border-primary transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-cargo" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest flex items-center gap-2">
                      <Shield size={12} className="text-primary" /> Cargo
                    </Label>
                    <Select 
                      value={userAccount.cargo} 
                      onValueChange={(val) => setUserAccount({...userAccount, cargo: val})}
                    >
                      <SelectTrigger className="bg-background border-border text-foreground focus:ring-primary">
                        <SelectValue placeholder="Selecione um cargo" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border text-foreground">
                        <SelectItem value="Dono" className="focus:bg-primary focus:text-white">Dono</SelectItem>
                        <SelectItem value="Tatuador" className="focus:bg-primary focus:text-white">Tatuador</SelectItem>
                        <SelectItem value="Recepcionista" className="focus:bg-primary focus:text-white">Recepcionista</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={handleSaveAccount}
                    className="bg-primary hover:bg-primary/90 text-white font-semibold h-11 px-8 shadow-lg shadow-primary/20 transition-all active:scale-95"
                  >
                    <Save className="mr-2 size-4" />
                    Salvar Alterações
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Password Change Card */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-foreground">Alterar Senha</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Mantenha sua conta segura com uma senha forte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="senha-atual" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest">Senha Atual</Label>
                    <div className="relative">
                      <Input 
                        id="senha-atual"
                        type={showPasswords.atual ? "text" : "password"}
                        value={passwordData.atual}
                        onChange={(e) => setPasswordData({...passwordData, atual: e.target.value})}
                        className="bg-background border-border text-foreground focus:border-primary transition-all pr-10"
                      />
                      <button 
                        onClick={() => setShowPasswords({...showPasswords, atual: !showPasswords.atual})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPasswords.atual ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nova-senha" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest">Nova Senha</Label>
                    <div className="relative">
                      <Input 
                        id="nova-senha"
                        type={showPasswords.nova ? "text" : "password"}
                        value={passwordData.nova}
                        onChange={(e) => setPasswordData({...passwordData, nova: e.target.value})}
                        className="bg-background border-border text-foreground focus:border-primary transition-all pr-10"
                      />
                      <button 
                        onClick={() => setShowPasswords({...showPasswords, nova: !showPasswords.nova})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPasswords.nova ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmar-senha" className="text-muted-foreground font-normal uppercase text-[10px] tracking-widest">Confirmar Nova Senha</Label>
                    <div className="relative">
                      <Input 
                        id="confirmar-senha"
                        type={showPasswords.confirmar ? "text" : "password"}
                        value={passwordData.confirmar}
                        onChange={(e) => setPasswordData({...passwordData, confirmar: e.target.value})}
                        className={cn(
                          "bg-background border-border text-foreground focus:border-primary transition-all pr-10",
                          passwordData.nova !== passwordData.confirmar && passwordData.confirmar !== "" && "border-red-500 focus:border-red-500"
                        )}
                      />
                      <button 
                        onClick={() => setShowPasswords({...showPasswords, confirmar: !showPasswords.confirmar})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPasswords.confirmar ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {passwordData.nova !== passwordData.confirmar && passwordData.confirmar !== "" && (
                      <p className="text-[10px] text-red-500 font-semibold flex items-center gap-1 mt-1">
                        <AlertCircle size={10} /> As senhas não coincidem
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={handleChangePassword}
                    className="bg-primary hover:bg-primary/90 text-white font-semibold h-11 px-8 shadow-lg shadow-primary/20 transition-all active:scale-95"
                  >
                    <Lock className="mr-2 size-4" />
                    Alterar Senha
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case "mensagens": {
        const messageSections = [
          { key: "confirmacao" as const, title: "Confirmação", description: "Enviada quando um agendamento é criado" },
          { key: "lembrete" as const, title: "Lembrete", description: "Enviada 24h antes do agendamento" },
          { key: "cuidados" as const, title: "Pós-Tattoo", description: "Enviada após a conclusão da sessão" },
          { key: "cicatrizacao" as const, title: "Cicatrização", description: "Enviada 15 dias após a sessão" },
          { key: "reativacao" as const, title: "Reativação", description: "Cliente sem agendamento há mais de 3 meses" },
          { key: "aniversario" as const, title: "Aniversário", description: "Enviada no dia do aniversário do cliente" },
        ];

        const variables = ["{nome_cliente}", "{data}", "{horario}", "{nome_tatuador}", "{valor}", "{sinal}", "{link_calendario}", "{link_avaliacao}"];

        const amostras: Record<string, string> = {
          "{nome_cliente}": "Maria Silva",
          "{data}": "22/10/2026",
          "{horario}": "09:00",
          "{nome_tatuador}": userAccount.nome || studioProfile.nome || "Seu Studio",
          "{valor}": "R$ 500,00",
          "{sinal}": "R$ 50,00",
          "{link_calendario}": "https://calendar.google.com/...",
          "{link_avaliacao}": studioProfile.linkAvaliacaoGoogle || "https://g.page/r/...",
        };

        const renderPreview = (texto: string) =>
          variables.reduce((acc, v) => acc.split(v).join(amostras[v]), texto);

        const secaoAtiva = messageSections.find((s) => s.key === mensagemAtiva) ?? messageSections[0];

        return (
          <div className="space-y-6 animate-in fade-in duration-500 max-w-5xl">
            <div>
              <h2 className="text-xl font-semibold text-foreground">WhatsApp Automático</h2>
              <p className="text-muted-foreground text-sm">Mensagens enviadas automaticamente aos seus clientes pelo WhatsApp do studio</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {messageSections.map((section) => (
                <button
                  key={section.key}
                  onClick={() => setMensagemAtiva(section.key)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wide border transition-all",
                    mensagemAtiva === section.key
                      ? "bg-primary border-primary text-white shadow-sm"
                      : "bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  )}
                >
                  {section.title}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-card border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold text-foreground">{secaoAtiva.title}</CardTitle>
                  <CardDescription className="text-muted-foreground text-xs">
                    {secaoAtiva.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    ref={textareaRefs[secaoAtiva.key]}
                    value={messages[secaoAtiva.key]}
                    onChange={(e) => setMessages({ ...messages, [secaoAtiva.key]: e.target.value })}
                    className="bg-background border-border text-foreground focus:border-primary min-h-[160px] resize-none text-sm leading-relaxed"
                    placeholder="Escreva sua mensagem aqui..."
                  />
                  <div className="flex flex-wrap gap-2">
                    {variables.map((variable) => (
                      <button
                        key={variable}
                        onClick={() => insertVariable(secaoAtiva.key, variable)}
                        className="text-[10px] font-semibold text-muted-foreground bg-background border border-border px-2 py-1 rounded hover:border-primary hover:text-primary transition-all flex items-center gap-1"
                      >
                        <Hash size={10} />
                        {variable}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2 px-1">Prévia do WhatsApp</p>
                <div className="bg-background border border-border rounded-2xl p-4">
                  <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
                    <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                      {renderPreview(messages[secaoAtiva.key]) || "Escreva sua mensagem ao lado para ver a prévia aqui."}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-2 pb-10">
              <Button
                onClick={handleSaveMessages}
                className="bg-primary hover:bg-primary/90 text-white font-semibold h-11 px-8 shadow-lg shadow-primary/20 transition-all active:scale-95 w-full md:w-auto"
              >
                <Save className="mr-2 size-4" />
                Salvar Mensagens
              </Button>
            </div>
          </div>
        );
      }
      case "assistente":
        return (
          <div className="space-y-6 animate-in fade-in duration-500 max-w-3xl">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Assistente</h2>
              <p className="text-muted-foreground text-sm">Conecte o WhatsApp do studio e permita que a IA gerencie agendamentos por mensagem</p>
            </div>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Sparkles className="text-primary size-4" />
                  Status &amp; Conexão
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-background border border-border/50 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">Ativar Assistente</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Permitir que a IA gerencie agendamentos</p>
                    </div>
                    <Switch
                      checked={assistenteAtivo}
                      disabled={isSavingAssistente}
                      onCheckedChange={handleToggleAssistente}
                    />
                  </div>
                  <div className="p-4 rounded-xl bg-background border border-border/50 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">WhatsApp</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {whatsappConectado ? "Conectado" : "Conexão para mensagens automáticas"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      disabled={whatsappConectado}
                      onClick={handleConectarWhatsapp}
                      className="bg-primary hover:bg-primary/90 text-white font-semibold shrink-0"
                    >
                      {whatsappConectado ? "Conectado" : "Conectar"}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4 leading-relaxed">
                  A conexão com o WhatsApp depende da aprovação da Meta e ainda está em implementação — em breve
                  você poderá conectar o número do studio por aqui.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Mail className="text-primary size-4" />
                  Notificações por e-mail
                </CardTitle>
                <CardDescription className="text-muted-foreground text-xs">
                  Avisos automáticos pra você: cliente preencheu a ficha, agendamento criado por um tatuador da
                  equipe, e lembrete 24h antes de cada sessão
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-xl bg-background border border-border/50 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Receber notificações por e-mail</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Enviadas para o e-mail da sua conta</p>
                  </div>
                  <Switch
                    checked={notificacoesEmailAtivo}
                    disabled={isSavingNotificacoes}
                    onCheckedChange={handleToggleNotificacoesEmail}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h1 className="text-3xl font-semibold text-foreground tracking-tight">Configurações</h1>
        <p className="text-muted-foreground">Gerencie as preferências do seu studio e conta</p>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Menu */}
        <aside className="w-full md:w-64 space-y-2">
          <nav className="flex flex-row md:flex-col overflow-x-auto md:overflow-visible pb-4 md:pb-0 gap-2 sticky top-24">
            {menuItems.map((item) => {
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 whitespace-nowrap md:w-full font-medium",
                    isActive 
                      ? "bg-primary text-foreground shadow-lg shadow-primary/20" 
                      : "text-muted-foreground hover:bg-card hover:text-white"
                  )}
                >
                  <item.icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content Area */}
        <main className="flex-1 min-w-0">
          {renderContent()}
        </main>
      </div>

      {/* Tatuador Dialog */}
      <Dialog
        open={isTatuadorDialogOpen}
        onOpenChange={(open) => {
          setIsTatuadorDialogOpen(open);
          if (!open) {
            setEditingTatuador(null);
            setConviteGerado(null);
          }
        }}
      >
        <DialogContent className="p-0 border-none bg-transparent max-w-lg shadow-none">
          {conviteGerado ? (
            <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full shadow-2xl space-y-4">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600">
                  <Send size={18} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Convite gerado</h2>
                  <p className="text-xs text-muted-foreground">Envie este link para o tatuador. Expira em 7 dias.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-background border border-border rounded-lg px-3 py-2.5 break-all select-all">
                  {conviteGerado}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(conviteGerado).catch(() => {});
                    toast.success("Link copiado!");
                  }}
                  className="shrink-0 size-9 rounded-lg bg-primary hover:bg-primary/90 text-white flex items-center justify-center"
                >
                  <Copy size={15} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                O link também fica disponível na lista da equipe enquanto o convite estiver pendente.
              </p>
              <Button
                onClick={() => {
                  setIsTatuadorDialogOpen(false);
                  setConviteGerado(null);
                }}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold h-11"
              >
                Concluir
              </Button>
            </div>
          ) : (
            <TatuadorForm
              title={editingTatuador ? "Editar tatuador" : "Convidar tatuador"}
              submitLabel={editingTatuador ? "Salvar alterações" : "Salvar e gerar convite"}
              initialData={editingTatuador}
              jaVinculado={!!editingTatuador?.tatuador_id}
              onSubmit={handleSaveTatuador}
              onCancel={() => {
                setIsTatuadorDialogOpen(false);
                setEditingTatuador(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Remover da equipe?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Esta ação removerá o acesso do tatuador ao studio. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-muted text-foreground border-border">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTatuador} className="bg-primary hover:bg-primary/90 text-white">
              Sim, remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
